// @ts-strict-ignore
import ErrorState from './ErrorState'
import Filters from './Filters'
import { Grid } from './Grid'
import LoadingCards from './LoadingCards'
import { reducer } from './reducer'
import { useUrlSync } from './useUrlSync'
import Pagination from '@components/Pagination'
import { ProductCard } from '@components/ProductCard'
import ProductGridCTABlock from '@components/ProductGridCTABlock'
import { breakpoints } from '@gassan-ui'
import {
  ArticleSortEnum,
  ArticlesFilterInput,
  ArticlesPaginationInput,
  useArticlesQuery,
} from '@generated'
import { articlesToDataLayerItems } from '@lib/data-layer-social-ads/helpers/articlesToDataLayerItems'
import { dataLayerItemList } from '@lib/data-layer-social-ads/view-item-list'
import { useWishlist } from '@lib/hooks/use-wishlist'
import { useBreakpoint } from '@lib/hooks/useBreakpoint'
import { ProductGridCallToActionContent } from '@lib/kontent/models/ProductGridCallToActionContent'
import { useRouter } from 'next/router'
import { FC, useEffect, useReducer, useRef, useState } from 'react'

const PAGE_SIZE = 24

type CallToAction = {
  index: number
  item: ProductGridCallToActionContent
}

type Props = {
  codename?: string
  type: string
  articleTypeID?: number
  theme?: string
  brands: number[]
  collections: number[]
  fixedBrands: number[]
  fixedCollections: number[]
  callToActions?: CallToAction[]
  initialPage?: number
  initialSort?: string
  webID?: string
  fixedFilters: {
    [key: number]: number[]
  }
  initialFilters?: {
    [key: number]: number[]
  }
  promotionLabel?: string
  isPreOwned: boolean
  onlyMemberDiscountProducts: boolean
  preProductBanner?: React.ReactNode
}

const ProductGrid: FC<Props> = ({
  codename,
  type,
  theme,
  brands,
  collections,
  fixedBrands,
  webID = 'gssnwsph',
  fixedCollections,
  articleTypeID,
  callToActions,
  fixedFilters,
  initialPage,
  initialSort,
  initialFilters,
  promotionLabel,
  isPreOwned,
  onlyMemberDiscountProducts,
  preProductBanner,
}) => {
  const firstPageCalltoActions = callToActions.filter((cta) => cta.index < PAGE_SIZE)

  const wishlist = useWishlist()
  const isDesktop = useBreakpoint(breakpoints.large)
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)
  const topRef = useRef<HTMLElement>()
  const [state, dispatch] = useReducer(reducer, {
    sort: initialSort,
    page: initialPage,
    filters: initialFilters,
    brands,
    collections,
  })
  const limit = state.page === 1 ? PAGE_SIZE - firstPageCalltoActions.length : PAGE_SIZE
  const attributeFilters = {
    ...state.filters,
    ...fixedFilters,
  }
  // Exclude Rolex products from the pre-owned page
  // Rolex products have their own page in the RCPO section
  let excludeCollections = []
  if (isPreOwned) {
    excludeCollections = [1]
  }

  const variables: {
    pagination: ArticlesPaginationInput
    filters: ArticlesFilterInput
  } = {
    pagination: {
      limit,
      skip: state.page === 1 ? 0 : (state.page - 1) * 24 - firstPageCalltoActions.length,
      sort: (state.sort ?? ArticleSortEnum.Populair) as ArticleSortEnum,
    },
    filters: {
      theme,
      brands: [...state.brands, ...fixedBrands],
      collections: [...state.collections, ...fixedCollections],
      excludeCollections,
      articleTypeID,
      webID,
      onlyMemberDiscountProducts,
      attributeFilters: Object.keys(attributeFilters)
        .map((key) =>
          attributeFilters[key].length > 0
            ? {
                attributeID: parseInt(key, 10),
                attributeValueID: attributeFilters[key],
              }
            : null,
        )
        .filter((v) => v),
    },
  }

  const [result] = useArticlesQuery({
    variables,
  })

  const total = result?.data?.articles?.total
  const articles = result?.data?.articles?.items
  const { asPath } = useRouter()
  const path = asPath.split('?')[0]
  useEffect(() => {
    // Set filters if the base path changes (e.g. omega -> cartier)
    dispatch({ type: 'set-sort', sort: initialSort })
    dispatch({ type: 'set-filters', filters: initialFilters })
    dispatch({ type: 'set-brands', value: brands })
    dispatch({ type: 'set-collections', value: collections })
    // This should remain last to prevent set-brands or set-collections resetting the page to 1
    dispatch({ type: 'set-page', page: initialPage })
  }, [path]) // eslint-disable-line

  useEffect(() => {
    if (total && !hasFetchedOnce) {
      setHasFetchedOnce(true)
    }
  }, [total]) // eslint-disable-line

  useEffect(() => {
    const dataLayerItems = articlesToDataLayerItems(articles)
    dataLayerItemList(dataLayerItems)
  }, [articles])

  // Normalize CTA's into {[index]: item}
  const ctasById: Record<number, ProductGridCallToActionContent> = {}

  if (state.page === 1) {
    firstPageCalltoActions.forEach(({ index, item }) => {
      ctasById[index] = item
    })
  }

  useUrlSync(state, fixedFilters)
  return (
    <>
      <Filters
        codename={codename}
        type={type}
        areProductsLoading={result.fetching}
        totalProducts={total}
        dispatch={dispatch}
        variables={variables}
        fixedFilters={fixedFilters}
        isPreOwned={isPreOwned}
        {...state}
      />
      {preProductBanner && preProductBanner}
      <Grid ref={topRef}>
        {result.fetching ? (
          <LoadingCards />
        ) : result.error ? (
          <ErrorState />
        ) : (
          <>
            {articles.map((article, index) => {
              const title = isPreOwned && article.collection ? article.collection : article.brand
              const subtitle = article.description
              const callToAction = ctasById[index]

              return (
                <>
                  {callToAction && (
                    <ProductGridCTABlock
                      key={`cta-${index}`}
                      title={callToAction.title.value}
                      href={callToAction.link_url.value}
                      mobileImage={callToAction.image_mobile.value[0]?.url}
                      desktopImage={callToAction.image_desktop.value[0]?.url}
                      contentPosition={
                        callToAction.title_position.value[0]?.codename as 'top' | 'bottom'
                      }
                      variant={callToAction.style.value[0]?.codename as 'light' | 'dark'}
                    />
                  )}
                  <ProductCard
                    promotionLabel={promotionLabel}
                    key={article.id}
                    brand={article.brand}
                    articleType={article.articleType}
                    description={article.description}
                    hidePrice={article.brand === 'TROPHY BY GASSAN' || !article.isPriceVisible}
                    id={article.id}
                    isOnWishlist={wishlist.articleIds.includes(article.id)}
                    onClickWishlist={() =>
                      wishlist.toggle({
                        id: article.id,
                        title,
                        subtitle,
                        image: article.images[0],
                      })
                    }
                    title={title}
                    subtitle={subtitle}
                    images={article.images}
                    price={article.price}
                    discountPrice={article.salePrice || undefined}
                    href={`/p/${article.urlSlug}`}
                  />
                </>
              )
            })}
          </>
        )}
      </Grid>
      {hasFetchedOnce && (
        <Pagination
          setPage={(page) => {
            dispatch({ type: 'set-page', page })
            if (topRef.current) {
              window.scrollTo({
                top: topRef.current.offsetTop - (isDesktop ? 250 : 65),
                left: 0,
                behavior: 'smooth',
              })
            }
          }}
          totalCount={result.data?.articles?.total || 0}
          limit={PAGE_SIZE}
          currentPage={state.page}
        />
      )}
    </>
  )
}

export default ProductGrid
